import { useEffect, useState } from "react";
import { clsx } from "clsx";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Badge } from "@/components/ui/Badge";
import type { Milestone, MilestoneState } from "../types";
import { fmtDate } from "../utils";
import { milestoneMeta, MILESTONE_NA_STYLE, MILESTONE_STATES } from "./ui";
import {
  MilestoneCounts,
  MilestoneFlagMarks,
  MilestoneProposalMark,
  MilestoneWodyPendingMark,
  MilestoneWodyStaleMark,
  wodyPendingFor,
  wodyStaleFor,
  type MilestoneFlags,
  type MilestoneProposal,
} from "./MilestoneCounts";

// Board columns, left→right in the natural progress flow; the applicability pair
// ("dotyczy" / "nie dotyczy") sits last, outside the progress flow. Kolejność,
// etykiety i kolory idą z jednej mapy stanów (./ui), wspólnej z siatką i grafem.
const COLUMN_STATES: MilestoneState[] = MILESTONE_STATES;

// Status kanban: one droppable column per MilestoneState. Dragging a card into another
// column PATCHes its `state` (the backend auto-stamps `completed_at` on → done). Within-
// column order is intentionally not draggable here — the grid view owns the global order.
export function MilestoneKanban({
  milestones,
  now,
  onSetState,
  onOpenDetails,
  onDelete,
  flagsByKey,
  proposalsByKey,
  onApplyProposal,
  applyingProposal,
  wodyPendingCount,
  wodyAnalysisStale,
}: {
  milestones: Milestone[];
  now: Date;
  onSetState: (mkey: string, state: MilestoneState, opts?: { onError?: () => void }) => void;
  onOpenDetails: (mkey: string) => void;
  onDelete: (m: Milestone) => void;
  /** Ostrzeżenie o kolejności / ścieżka krytyczna z grafu procesu (te same znaki). */
  flagsByKey?: Map<string, MilestoneFlags>;
  /** Niebieskie propozycje z faktów, po kluczu kroku — plakietka na karcie. */
  proposalsByKey?: Map<string, MilestoneProposal>;
  onApplyProposal?: (p: MilestoneProposal) => void;
  applyingProposal?: boolean;
  /** Wody Polskie: ile zdarzeń czeka na zlecenie podwykonawcy. */
  wodyPendingCount?: number;
  wodyAnalysisStale?: boolean;
}) {
  // Optimistic column override (mkey → state) so a dropped card jumps immediately; it's
  // reconciled away once the server refetch confirms the new state, and reverted on error.
  const [override, setOverride] = useState<Record<string, MilestoneState>>({});
  const [activeKey, setActiveKey] = useState<string | null>(null);

  useEffect(() => {
    setOverride((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      const server = new Map(milestones.map((m) => [m.key, m.state]));
      let changed = false;
      const next = { ...prev };
      for (const k of keys) {
        // Drop the override once the server agrees (write landed) or the card is gone.
        if (!server.has(k) || server.get(k) === prev[k]) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [milestones]);

  const sensors = useSensors(
    // 5px threshold so a click (→ details) never turns into an accidental drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const effState = (m: Milestone): MilestoneState => override[m.key] ?? m.state;
  const byKey = new Map(milestones.map((m) => [m.key, m]));

  const onDragStart = (e: DragStartEvent) => setActiveKey(String(e.active.id));
  const onDragCancel = () => setActiveKey(null);
  const onDragEnd = (e: DragEndEvent) => {
    setActiveKey(null);
    const { active, over } = e;
    if (!over) return;
    const mkey = String(active.id);
    const dest = String(over.id) as MilestoneState; // only columns are droppable → id is a state
    const m = byKey.get(mkey);
    if (!m || !COLUMN_STATES.includes(dest) || effState(m) === dest) return;
    setOverride((p) => ({ ...p, [mkey]: dest }));
    onSetState(mkey, dest, {
      onError: () =>
        setOverride((p) => {
          const n = { ...p };
          delete n[mkey];
          return n;
        }),
    });
  };

  const active = activeKey ? byKey.get(activeKey) ?? null : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {COLUMN_STATES.map((s) => {
          const items = milestones.filter((m) => effState(m) === s);
          return (
            <KanbanColumn key={s} state={s} count={items.length}>
              {items.map((m) => (
                <KanbanCard
                  key={m.key}
                  m={m}
                  now={now}
                  onOpenDetails={() => onOpenDetails(m.key)}
                  onDelete={m.is_custom ? () => onDelete(m) : undefined}
                  dragging={activeKey === m.key}
                  flags={flagsByKey?.get(m.key)}
                  proposal={proposalsByKey?.get(m.key)}
                  onApplyProposal={onApplyProposal}
                  applyingProposal={applyingProposal}
                  wodyPending={wodyPendingFor(m.key, wodyPendingCount)}
                  wodyStale={wodyStaleFor(m.key, wodyAnalysisStale)}
                />
              ))}
            </KanbanColumn>
          );
        })}
      </div>
      <DragOverlay dropAnimation={null}>
        {active ? <KanbanCardView m={active} now={now} overlay /> : null}
      </DragOverlay>
    </DndContext>
  );
}

// ── droppable status column ────────────────────────────────────────────────────
function KanbanColumn({
  state,
  count,
  children,
}: {
  state: MilestoneState;
  count: number;
  children: React.ReactNode;
}) {
  const meta = milestoneMeta(state);
  const { setNodeRef, isOver } = useDroppable({ id: state });
  return (
    <div className="flex min-w-[220px] flex-1 flex-col">
      <div
        className="flex items-center justify-between gap-2 border-b-2 px-1 pb-1.5"
        style={{ borderColor: milestoneMeta(state).accent }}
      >
        <Badge color={meta.color}>{meta.label}</Badge>
        <span className="font-mono text-xs font-bold tabular-nums text-[var(--color-text-muted)]">
          {count}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={clsx(
          "mt-2 flex min-h-[140px] flex-1 flex-col gap-2 border border-dashed p-2 transition-colors",
          isOver
            ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
            : "border-[var(--color-border-muted)]",
        )}
      >
        {count === 0 ? (
          <div className="flex flex-1 items-center justify-center py-6 text-center font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
            upuść tutaj
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

// ── draggable wrapper (drag lives on the wrapper; the view handles clicks) ──────
function KanbanCard({
  m,
  now,
  onOpenDetails,
  onDelete,
  dragging,
  flags,
  proposal,
  onApplyProposal,
  applyingProposal,
  wodyPending,
  wodyStale,
}: {
  m: Milestone;
  now: Date;
  onOpenDetails: () => void;
  onDelete?: () => void;
  dragging?: boolean;
  flags?: MilestoneFlags;
  proposal?: MilestoneProposal;
  onApplyProposal?: (p: MilestoneProposal) => void;
  applyingProposal?: boolean;
  wodyPending?: number;
  /** Wody Polskie: detekcja policzona na starszej wersji trasy. */
  wodyStale?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: m.key });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Translate.toString(transform),
        opacity: dragging ? 0.3 : undefined,
      }}
    >
      <KanbanCardView
        m={m}
        now={now}
        onOpenDetails={onOpenDetails}
        onDelete={onDelete}
        flags={flags}
        proposal={proposal}
        onApplyProposal={onApplyProposal}
        applyingProposal={applyingProposal}
        wodyPending={wodyPending}
        wodyStale={wodyStale}
      />
    </div>
  );
}

// ── card visuals (shared by the live card and the drag overlay) ─────────────────
function KanbanCardView({
  m,
  now,
  onOpenDetails,
  onDelete,
  overlay,
  flags,
  proposal,
  onApplyProposal,
  applyingProposal,
  wodyPending,
  wodyStale,
}: {
  m: Milestone;
  now: Date;
  onOpenDetails?: () => void;
  onDelete?: () => void;
  overlay?: boolean;
  flags?: MilestoneFlags;
  proposal?: MilestoneProposal;
  onApplyProposal?: (p: MilestoneProposal) => void;
  applyingProposal?: boolean;
  wodyPending?: number;
  /** Wody Polskie: detekcja policzona na starszej wersji trasy. */
  wodyStale?: boolean;
}) {
  const overdue =
    !!m.due_date && m.state !== "done" && m.state !== "na" && new Date(m.due_date) < now;
  return (
    <div
      onClick={onOpenDetails}
      role="button"
      tabIndex={overlay ? -1 : 0}
      onKeyDown={(e) => {
        // Enter/Space open details — only from the card itself, not an inner control.
        if (onOpenDetails && (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
          e.preventDefault();
          onOpenDetails();
        }
      }}
      title="Przeciągnij, aby zmienić status · kliknij, aby otworzyć szczegóły"
      className={clsx(
        "card flex cursor-grab flex-col gap-2 p-2.5 active:cursor-grabbing focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-primary)]",
        overlay ? "shadow-hard-red rotate-1" : "hover:shadow-hard-red",
      )}
      style={{
        borderLeft: `3px solid ${m.state === "na" ? "#4b5563" : milestoneMeta(m.state).accent}`,
        ...(m.state === "na" ? MILESTONE_NA_STYLE : null),
      }}
    >
      {/* title row: state dot · type · (custom tag) · (delete, custom only) */}
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="h-2 w-2 shrink-0" style={{ background: milestoneMeta(m.state).accent }} aria-hidden />
        <div
          className="truncate font-mono text-[0.7rem] font-bold uppercase tracking-wide text-[var(--color-text-primary)]"
          title={m.type_display}
        >
          {m.type_display}
        </div>
        {/* Kamień z ustaleń „karty specyfiki" jest technicznie własny, ale NIE dodał go
            człowiek na tym projekcie — podpisujemy go źródłem, żeby nikt go nie skasował
            w przekonaniu, że to czyjaś ręczna notatka. */}
        {m.care_arrangement ? (
          <span
            className="shrink-0 border border-amber-500 px-1 font-mono text-[8px] uppercase leading-tight tracking-wide text-amber-700 dark:text-amber-400"
            title={`Kamień z ustaleń oddziału klienta (${m.care_source ?? "karta specyfiki"}) — zakładka „Opieka”`}
          >
            z ustaleń
          </span>
        ) : m.is_custom && (
          <span
            className="shrink-0 border border-[var(--color-border)] px-1 font-mono text-[8px] uppercase leading-tight tracking-wide text-[var(--color-text-muted)]"
            title="Kamień dodany ręcznie"
          >
            własny
          </span>
        )}
        <MilestoneFlagMarks flags={flags} />
        <MilestoneWodyPendingMark count={wodyPending} />
        <MilestoneWodyStaleMark stale={wodyStale} />
        <MilestoneProposalMark
          proposal={proposal}
          onApply={onApplyProposal}
          disabled={applyingProposal}
        />
        {onDelete && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label="Usuń kamień milowy"
            title="Usuń kamień milowy"
            className="ml-auto shrink-0 px-0.5 text-sm leading-none text-[var(--color-text-muted)] hover:text-[var(--color-primary)] focus:outline-none"
          >
            ×
          </button>
        )}
      </div>

      {/* surfaced dates: completion for done items, otherwise the deadline */}
      {m.state === "done" && m.completed_at ? (
        <div className="flex items-center gap-1 font-mono text-[10px] text-emerald-700">
          <span aria-hidden>✓</span>
          <span>ukończono {fmtDate(m.completed_at)}</span>
        </div>
      ) : m.due_date ? (
        <div
          className={clsx(
            "font-mono text-[10px]",
            overdue ? "text-[var(--color-primary)] font-bold" : "text-[var(--color-text-muted)]",
          )}
        >
          termin: {fmtDate(m.due_date)}
          {overdue && " · po terminie"}
        </div>
      ) : null}

      {/* details affordance (notes & files live in the modal) */}
      {onOpenDetails && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails();
          }}
          title="Notatki i pliki"
          className="mt-auto flex w-full items-center justify-between gap-1 border-t border-[var(--color-border-muted)] pt-1.5 font-mono text-[11px] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-primary)]"
        >
          <MilestoneCounts notes={m.note_count} files={m.file_count} />
          <span aria-hidden>szczegóły →</span>
        </button>
      )}
    </div>
  );
}
